import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, Eye, EyeOff, Star, Upload, X, Image as ImageIcon,
  Play, Calendar, Link as LinkIcon, FolderOpen, Loader2, CheckSquare,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";

const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const CATEGORIES = [
  "training", "workshop", "certification", "award", "partnership",
  "milestone", "event", "community", "recognition", "other",
];
const MONTHS = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SI = "px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 bg-white";

const BLANK = {
  title: "", subtitle: "", description: "", achievement_month: 0, achievement_year: new Date().getFullYear(),
  category: "other", tags: [], featured: false, display_order: 0, published: false,
  cover_image: "", thumbnail: "", featured_image: "", images: [], youtube_urls: [], drive_links: [],
};

function Badge({ children, color = "gray" }) {
  const c = { green: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", gray: "bg-gray-100 text-gray-600", blue: "bg-blue-100 text-blue-700" };
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${c[color] || c.gray}`}>{children}</span>;
}

/* ── Single image upload ── */
function MediaUpload({ label, value, onChange }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/achievements/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-700">{label}</label>
      <div className="flex items-center gap-2 flex-wrap">
        {value && (
          <div className="relative w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 shrink-0">
            <img src={resolve(value)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange("")} className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white rounded-full text-[10px] flex items-center justify-center">×</button>
          </div>
        )}
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-1.5 text-sm border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg transition disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" />{uploading ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      </div>
    </div>
  );
}

/* ── Multi image upload with featured selector ── */
function MultiImageUpload({ label, urls, featuredImage, onChange, onSetFeatured }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  async function handleFiles(files) {
    if (!files?.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/achievements/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(data.url);
      }
      onChange([...urls, ...newUrls]);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }
  const remove = (idx) => {
    const removed = urls[idx];
    onChange(urls.filter((_, i) => i !== idx));
    if (featuredImage === removed) onSetFeatured("");
  };
  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-gray-700">{label}</label>
      <ImageGrid urls={urls} featuredImage={featuredImage} onSetFeatured={onSetFeatured} onRemove={remove} />
      {urls.length > 0 && <p className="text-[10px] text-gray-400">Click any image to set it as featured (used on homepage and cards).</p>}
      <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
        className="inline-flex items-center gap-1.5 text-xs border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg transition disabled:opacity-50">
        <Upload className="w-3.5 h-3.5" />{uploading ? "Uploading…" : "Add Images"}
      </button>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(Array.from(e.target.files || []))} />
    </div>
  );
}

/* ── Shared image grid with featured selector ── */
function ImageGrid({ urls, featuredImage, onSetFeatured, onRemove }) {
  if (!urls?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {urls.map((u, i) => {
        const src = u.startsWith("http") ? u : resolve(u);
        const isFeat = featuredImage === u;
        return (
          <div key={i}
            className={`relative w-20 h-20 rounded-lg border-2 overflow-hidden bg-gray-50 shrink-0 cursor-pointer group ${isFeat ? "border-amber-400 ring-2 ring-amber-300" : "border-gray-200 hover:border-indigo-300"}`}
            onClick={() => onSetFeatured(isFeat ? "" : u)}
            title={isFeat ? "Featured (click to unset)" : "Click to set as featured"}>
            <img src={src} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
            {isFeat && <div className="absolute bottom-0 left-0 right-0 bg-amber-500/90 text-white text-[9px] font-semibold text-center py-0.5">Featured</div>}
            {onRemove && (
              <button onClick={e => { e.stopPropagation(); onRemove(i); }}
                className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white rounded-full text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition">×</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Google Drive Folder Browser ── */
function DriveFolderBrowser({ onSelectImages, featuredImage, onSetFeatured }) {
  const [folderUrl, setFolderUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState("");

  async function fetchFolder() {
    if (!folderUrl.trim()) return;
    setLoading(true); setError(""); setFiles([]);
    try {
      const { data } = await api.post("/admin/achievements/drive-folder", { folder_url: folderUrl.trim() });
      setFiles(data.files || []);
      if (!data.files?.length) setError("No images found. Make sure the folder is public and contains images.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not fetch folder. Ensure it is shared as 'Anyone with the link'.");
    }
    finally { setLoading(false); }
  }

  function toggleSelect(file) {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(file.id) ? n.delete(file.id) : n.add(file.id);
      return n;
    });
  }

  function addSelected() {
    const urls = files.filter(f => selected.has(f.id)).map(f => f.direct_url);
    if (!urls.length) { toast.error("Select at least one image"); return; }
    onSelectImages(urls);
    toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} added`);
    setSelected(new Set());
  }

  return (
    <div className="space-y-3 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
      <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
        <FolderOpen className="w-3.5 h-3.5 text-blue-500" />Google Drive Folder
      </label>
      <p className="text-[10px] text-gray-400">Paste a Google Drive folder URL (must be shared as "Anyone with the link can view").</p>
      <div className="flex gap-2">
        <input className={SI} value={folderUrl} onChange={e => setFolderUrl(e.target.value)}
          placeholder="https://drive.google.com/drive/folders/..." onKeyDown={e => e.key === "Enter" && fetchFolder()} />
        <button type="button" onClick={fetchFolder} disabled={loading || !folderUrl.trim()}
          className="shrink-0 inline-flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition disabled:opacity-50">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5" />}
          {loading ? "Loading…" : "Browse"}
        </button>
      </div>

      {error && <p className="text-xs text-rose-500">{error}</p>}

      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{files.length} image{files.length > 1 ? "s" : ""} found — click to select</span>
            <button type="button" onClick={() => setSelected(new Set(files.map(f => f.id)))}
              className="text-[10px] text-indigo-600 hover:underline">Select all</button>
          </div>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {files.map(f => {
              const isSel = selected.has(f.id);
              const isFeat = featuredImage === f.direct_url;
              return (
                <div key={f.id} onClick={() => toggleSelect(f)}
                  className={`relative w-20 h-20 rounded-lg border-2 overflow-hidden bg-gray-50 shrink-0 cursor-pointer group ${isSel ? "border-indigo-500 ring-2 ring-indigo-300" : "border-gray-200 hover:border-indigo-300"}`}>
                  <img src={f.thumbnail_url} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.background = "#f3f4f6"; e.target.style.display = "none"; }} />
                  {isSel && <div className="absolute inset-0 bg-indigo-600/20 flex items-center justify-center"><CheckSquare className="w-5 h-5 text-indigo-700 drop-shadow" /></div>}
                  {isFeat && <div className="absolute bottom-0 left-0 right-0 bg-amber-500/90 text-white text-[9px] font-semibold text-center py-0.5">Featured</div>}
                </div>
              );
            })}
          </div>
          {selected.size > 0 && (
            <button type="button" onClick={addSelected}
              className="inline-flex items-center gap-1.5 text-xs bg-[#0F172A] hover:bg-[#1e293b] text-white px-4 py-2 rounded-lg transition">
              <Plus className="w-3.5 h-3.5" />Add {selected.size} selected image{selected.size > 1 ? "s" : ""} to gallery
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── YouTube URL list ── */
function YouTubeList({ urls, onChange }) {
  const [input, setInput] = useState("");
  function getYouTubeId(url) {
    const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
  }
  const add = () => {
    const v = input.trim();
    if (v && (v.includes("youtube") || v.includes("youtu.be"))) {
      onChange([...urls, v]); setInput("");
    } else toast.error("Enter a valid YouTube URL");
  };
  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-gray-700">YouTube URLs</label>
      {urls.map((u, i) => {
        const vid = getYouTubeId(u);
        return (
          <div key={i} className="flex items-center gap-2">
            {vid && (
              <div className="relative w-16 h-10 rounded overflow-hidden bg-gray-100 shrink-0">
                <img src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play className="w-3 h-3 text-white" fill="white" />
                </div>
              </div>
            )}
            <span className="truncate flex-1 text-xs text-gray-600">{u}</span>
            <button onClick={() => onChange(urls.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-600 shrink-0"><X className="w-3 h-3" /></button>
          </div>
        );
      })}
      <div className="flex gap-2">
        <input className={SI} placeholder="https://youtube.com/watch?v=..." value={input}
          onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} />
        <button type="button" onClick={add} className="shrink-0 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg">Add</button>
      </div>
    </div>
  );
}

/* ── Google Drive video links ── */
function DriveLinks({ urls, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (v && v.includes("drive.google.com")) { onChange([...urls, v]); setInput(""); }
    else toast.error("Enter a valid Google Drive URL");
  };
  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-gray-700">Google Drive Video Links</label>
      <p className="text-[10px] text-gray-400">Share link to a Drive video file (must be "Anyone with link can view").</p>
      {urls.map((u, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <Play className="w-3 h-3 text-blue-500 shrink-0" />
          <span className="truncate flex-1 text-gray-600">{u}</span>
          <button onClick={() => onChange(urls.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-600"><X className="w-3 h-3" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <input className={SI} placeholder="https://drive.google.com/file/d/.../view" value={input}
          onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} />
        <button type="button" onClick={add} className="shrink-0 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg">Add</button>
      </div>
    </div>
  );
}

/* ── Achievement Form ── */
function AchievementForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    ...BLANK, ...initial,
    tags: initial?.tags || [],
    images: initial?.images || [],
    youtube_urls: initial?.youtube_urls || [],
    drive_links: initial?.drive_links || [],
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [imgSource, setImgSource] = useState("upload"); // "upload" | "drive"
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function submit() {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const { data } = initial?.id
        ? await api.put(`/admin/achievements/${initial.id}`, form)
        : await api.post("/admin/achievements", form);
      toast.success(initial?.id ? "Updated" : "Created");
      onSave(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) { set("tags", [...form.tags, t]); setTagInput(""); }
  };

  function addDriveImages(urls) {
    // Merge new Drive image URLs into gallery; auto-set first as featured if none set
    const merged = [...new Set([...form.images, ...urls])];
    set("images", merged);
    if (!form.featured_image && urls.length) set("featured_image", urls[0]);
  }

  return (
    <AdminModal onClose={onClose} title={initial?.id ? "Edit Achievement" : "New Achievement"} maxWidth="max-w-2xl"
      footer={
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-[#0F172A] text-white rounded-lg hover:bg-[#1e293b] disabled:opacity-60 inline-flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5" />{saving ? "Saving…" : "Save"}
          </button>
        </div>
      }>
      <div className="space-y-5">

        {/* ── General ── */}
        <div className="space-y-3">
          <SectionTitle>General</SectionTitle>
          <Field label="Title *">
            <input className={SI} value={form.title} onChange={e => set("title", e.target.value)} placeholder="NEP 2020 Workshop — 500 Teachers Trained" />
          </Field>
          <Field label="Subtitle">
            <input className={SI} value={form.subtitle} onChange={e => set("subtitle", e.target.value)} placeholder="Partnership with CBSE Regional Office" />
          </Field>
          <Field label="Description">
            <textarea className={`${SI} resize-y`} rows={3} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Detailed description..." />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Month">
              <select className={SI} value={form.achievement_month} onChange={e => set("achievement_month", parseInt(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i} value={i}>{m || "—"}</option>)}
              </select>
            </Field>
            <Field label="Year">
              <input type="number" className={SI} value={form.achievement_year} onChange={e => set("achievement_year", parseInt(e.target.value) || 2024)} />
            </Field>
            <Field label="Category">
              <select className={SI} value={form.category} onChange={e => set("category", e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Order">
              <input type="number" className={SI} value={form.display_order} onChange={e => set("display_order", parseInt(e.target.value) || 0)} />
            </Field>
          </div>
          <div className="flex flex-wrap gap-4">
            <Toggle label="Published" checked={form.published} onChange={v => set("published", v)} />
            <Toggle label="Featured" checked={form.featured} onChange={v => set("featured", v)} />
          </div>
          <Field label="Tags">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                  {t}<button onClick={() => set("tags", form.tags.filter((_, j) => j !== i))}><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className={SI} placeholder="Add tag..." value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} />
              <button type="button" onClick={addTag} className="shrink-0 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg">Add</button>
            </div>
          </Field>
        </div>

        {/* ── Media — Cover / Thumbnail ── */}
        <div className="border-t border-gray-100 pt-4 space-y-4">
          <SectionTitle>Cover & Thumbnail</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <MediaUpload label="Cover Image" value={form.cover_image} onChange={v => set("cover_image", v)} />
            <MediaUpload label="Thumbnail" value={form.thumbnail} onChange={v => set("thumbnail", v)} />
          </div>
        </div>

        {/* ── Gallery Images ── */}
        <div className="border-t border-gray-100 pt-4 space-y-4">
          <SectionTitle>Gallery Images</SectionTitle>

          {/* Source toggle */}
          <div className="flex gap-2">
            {[["upload", "Upload Files"], ["drive", "Google Drive Folder"]].map(([key, lbl]) => (
              <button key={key} type="button" onClick={() => setImgSource(key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${imgSource === key ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                {lbl}
              </button>
            ))}
          </div>

          {imgSource === "upload" ? (
            <MultiImageUpload
              label="Upload Images (click to set as featured)"
              urls={form.images}
              featuredImage={form.featured_image}
              onChange={v => set("images", v)}
              onSetFeatured={v => set("featured_image", v)}
            />
          ) : (
            <DriveFolderBrowser
              onSelectImages={addDriveImages}
              featuredImage={form.featured_image}
              onSetFeatured={v => set("featured_image", v)}
            />
          )}

          {/* Show current gallery always */}
          {form.images.length > 0 && imgSource === "drive" && (
            <div className="space-y-1">
              <p className="text-xs text-gray-500 font-medium">Current gallery ({form.images.length} images)</p>
              <ImageGrid
                urls={form.images}
                featuredImage={form.featured_image}
                onSetFeatured={v => set("featured_image", v)}
                onRemove={idx => {
                  const removed = form.images[idx];
                  set("images", form.images.filter((_, i) => i !== idx));
                  if (form.featured_image === removed) set("featured_image", "");
                }}
              />
            </div>
          )}
        </div>

        {/* ── Videos ── */}
        <div className="border-t border-gray-100 pt-4 space-y-4">
          <SectionTitle>Videos</SectionTitle>
          <YouTubeList urls={form.youtube_urls} onChange={v => set("youtube_urls", v)} />
          <DriveLinks urls={form.drive_links} onChange={v => set("drive_links", v)} />
        </div>

        {/* ── SEO ── */}
        <div className="border-t border-gray-100 pt-4 space-y-3">
          <SectionTitle>SEO</SectionTitle>
          <Field label="SEO Title">
            <input className={SI} value={form.seo_title || ""} onChange={e => set("seo_title", e.target.value)} placeholder="Optional — defaults to title" />
          </Field>
          <Field label="SEO Description">
            <textarea className={`${SI} resize-none`} rows={2} value={form.seo_description || ""} onChange={e => set("seo_description", e.target.value)} />
          </Field>
        </div>

      </div>
    </AdminModal>
  );
}

/* ── Tiny helpers ── */
function SectionTitle({ children }) {
  return <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-700">{label}</label>
      {children}
    </div>
  );
}
function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-indigo-600 w-4 h-4" />
      {label}
    </label>
  );
}

/* ── Main page ── */
export default function AdminAchievements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState("all");

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/admin/achievements"); setItems(data || []); }
    catch { toast.error("Failed to load achievements"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function onSaved(data) {
    setItems(prev => {
      const idx = prev.findIndex(a => a.id === data.id);
      if (idx >= 0) { const a = [...prev]; a[idx] = data; return a; }
      return [data, ...prev];
    });
    setModal(null);
  }

  async function deleteItem(item) {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try { await api.delete(`/admin/achievements/${item.id}`); setItems(prev => prev.filter(a => a.id !== item.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  }

  async function togglePublished(item) {
    try {
      const { data } = await api.put(`/admin/achievements/${item.id}`, { published: !item.published });
      setItems(prev => prev.map(a => a.id === data.id ? data : a));
    } catch { toast.error("Update failed"); }
  }

  const filtered = filter === "all" ? items
    : filter === "published" ? items.filter(a => a.published)
    : filter === "featured" ? items.filter(a => a.featured)
    : items.filter(a => !a.published);

  if (loading) return (
    <div className="fade-up">
      <Header title="Achievements" subtitle="Manage milestones, awards, and accomplishments." />
      <div className="mt-6 space-y-3 max-w-4xl">{[1, 2, 3].map(n => <div key={n} className="h-20 rounded-2xl bg-gray-100 animate-pulse" />)}</div>
    </div>
  );

  return (
    <div className="fade-up pb-12">
      {modal !== null && <AchievementForm initial={modal} onSave={onSaved} onClose={() => setModal(null)} />}
      <Header title="Achievements"
        subtitle={`${items.length} achievement${items.length !== 1 ? "s" : ""} — displayed on homepage and achievements page.`}
        actions={
          <button onClick={() => setModal({})} className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] transition">
            <Plus className="w-4 h-4" /> Add Achievement
          </button>
        }
      />
      <div className="flex gap-2 mt-4 flex-wrap">
        {["all", "published", "draft", "featured"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${filter === f ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div className="mt-5 max-w-4xl">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No achievements yet. Create your first one.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(item => (
              <div key={item.id} className={`rounded-xl border border-gray-200 bg-white p-4 flex gap-4 items-start hover:shadow-sm transition ${!item.published ? "opacity-70" : ""}`}>
                <div className="w-20 h-16 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                  {(item.featured_image || item.cover_image || item.thumbnail || item.images?.[0]) ? (
                    <img src={resolve(item.featured_image || item.thumbnail || item.cover_image || item.images?.[0])} alt="" className="w-full h-full object-cover" />
                  ) : (item.youtube_urls?.[0]) ? (
                    <div className="relative w-full h-full">
                      <img src={`https://img.youtube.com/vi/${item.youtube_urls[0].match(/[a-zA-Z0-9_-]{11}/)?.[0]}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center"><Play className="w-4 h-4 text-white" fill="white" /></div>
                    </div>
                  ) : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-gray-300" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800 truncate">{item.title}</span>
                    {item.featured && <Badge color="amber">Featured</Badge>}
                    <Badge color={item.published ? "green" : "gray"}>{item.published ? "Published" : "Draft"}</Badge>
                    <Badge color="blue">{item.category}</Badge>
                  </div>
                  {item.subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{item.subtitle}</p>}
                  <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{MONTHS[item.achievement_month] || ""} {item.achievement_year}</span>
                    {item.images?.length > 0 && <span>{item.images.length} image{item.images.length !== 1 ? "s" : ""}</span>}
                    {item.youtube_urls?.length > 0 && <span>{item.youtube_urls.length} YouTube</span>}
                    {item.drive_links?.length > 0 && <span>{item.drive_links.length} Drive</span>}
                    {item.featured_image && <Badge color="amber">Featured image set</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => togglePublished(item)} title={item.published ? "Unpublish" : "Publish"}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                    {item.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => setModal(item)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                    <Save className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteItem(item)} className="p-1.5 rounded hover:bg-rose-50 text-gray-400 hover:text-rose-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
